/**
 * useFeatureData.hook.ts
 *
 * Custom React hook for fetching and managing feature data for a customer view.
 *
 * Features:
 * - Accepts optional tenantId, productFamily, and productName to filter data.
 * - Uses a toggleable fetch trigger (shouldFetch) to control when data is fetched.
 * - Fetches feature data using a Redux Toolkit query hook (useGetFeaturesDataQuery).
 * - Handles loading, error, and data states.
 * - Sorts and filters the fetched data based on productName.
 *   - Special handling for "Microsoft Intune": includes all features' data.
 *   - For other products: filters features matching the productName.
 * - Extracts and sets all feature names for selection.
 * - Avoids unnecessary state updates by comparing previous and new values.
 * - Logs telemetry events for fetch triggers, errors, successes, and feature selection changes.
 * - Exposes:
 *   - error: error state from the query.
 *   - isLoading: loading state from the query.
 *   - rawFeaturesData: the processed features data array.
 *   - selectedFeatures: array of selected feature names.
 *   - setSelectedFeatures: setter for selected features.
 *   - triggerFetch: function to manually trigger data fetch.
 */
import { useCallback, useEffect, useState } from "react";
import { FeaturesData } from "../ExploreFeatures.interfaces";
import { logEvent, logException } from "../../../helper/AppInsightsHelper";
import { arrSortDescByKeyLength } from "../../../Utils/js-common";
import { IArrayKey } from "../../../interfaces/CarouselProps.interfaces";

export interface UseFeatureDataProps {
  customProps: {
    pageName: string;
    tabName: string;
    apiName: string;
  }
  idProps: {
    tenantId: string;
    partnerOneId: string;
    mpnId: string;
  };
  productFamily?: string;
  productName?: string;
  data: any; // This can be typed more specifically based on the expected data structure
  isLoading: boolean;
  error: any; // This can be typed more specifically based on the expected error structure
}

export const useFeatureData = ({ customProps, idProps, productFamily, productName, data, isLoading, error }: UseFeatureDataProps) => {
  const { tenantId, partnerOneId, mpnId } = idProps;
  const [rawFeaturesData, setRawFeaturesData] = useState<FeaturesData[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);

  const triggerFetch = useCallback(() => {
    logEvent(customProps.tabName, customProps.pageName, "ExploreFeatures_" + customProps.apiName, {
      eventName: "FeatureData_FetchTriggered",
      hookName: "useFeatureData",
      tenantId,
      partnerOneId,
      mpnId,
      productFamily,
      productName
    });
  }, [tenantId, partnerOneId, mpnId, productFamily, productName]);

  useEffect(() => {
    if (error) {
      logException(customProps.pageName, customProps.tabName, customProps.apiName, error);
      logEvent(customProps.tabName, customProps.pageName, "ExploreFeatures_" + customProps.apiName, {
        eventName: "FeatureData_FetchError",
        hookName: "useFeatureData",
        tenantId,
        partnerOneId,
        mpnId,
        productFamily,
        productName,
        error: String(error)
      });
      return;
    }
    if (!data) return;

    // Deep clone and sort data
    const sortedData = arrSortDescByKeyLength({
      key: "data",
      data: JSON.parse(JSON.stringify(data))
    } as IArrayKey);

    // Filter features based on productName
    const features =
      productName === "Microsoft Intune" ? [...sortedData] : sortedData.filter((f) => f.name === productName);

    // Only update state if changed
    setRawFeaturesData((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(features)) {
        return features;
      }
      return prev;
    });

    const allFeatureNames = features.flatMap((feature) => (feature.data || []).map((f: { name: string }) => f.name));

    setSelectedFeatures((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(allFeatureNames)) {
        return allFeatureNames;
      }
      return prev;
    });

    logEvent(customProps.tabName, customProps.pageName, "ExploreFeatures_" + customProps.apiName, {
      eventName: "FeatureData_FetchSuccess",
      hookName: "useFeatureData",
      tenantId,
      partnerOneId,
      mpnId,
      productFamily,
      productName,
      featureCount: features.length,
      allFeatureNames
    });
  }, [data, error, productName, tenantId, partnerOneId, mpnId, productFamily]);

  // Log when selected features change
  useEffect(() => {
    logEvent(customProps.tabName, customProps.pageName, "ExploreFeatures_" + customProps.apiName, {
      eventName: "FeatureData_SelectedFeaturesChanged",
      hookName: "useFeatureData",
      tenantId,
      partnerOneId,
      mpnId,
      productFamily,
      productName,
      selectedFeatures
    });
  }, [selectedFeatures, tenantId, partnerOneId, mpnId, productFamily, productName]);

  return {
    error,
    isLoading,
    rawFeaturesData,
    selectedFeatures,
    setSelectedFeatures,
    triggerFetch
  };
};
